/** Small WebGL2 helpers (programs, textures, render targets). */

export class GLError extends Error {}

export interface Program {
  program: WebGLProgram;
  uniforms: Map<string, WebGLUniformLocation>;
  attribs: Map<string, number>;
}

function compile(gl: WebGL2RenderingContext, type: number, source: string, name: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new GLError(`Failed to compile lighting shader "${name}"`);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new GLError(`Failed to compile lighting shader "${name}": ${log}`);
  }
  return shader;
}

export function createProgram(gl: WebGL2RenderingContext, name: string, vs: string, fs: string): Program {
  const program = gl.createProgram();
  if (!program) throw new GLError(`Failed to compile lighting shader "${name}"`);
  const v = compile(gl, gl.VERTEX_SHADER, vs, `${name}.vert`);
  const f = compile(gl, gl.FRAGMENT_SHADER, fs, `${name}.frag`);
  gl.attachShader(program, v);
  gl.attachShader(program, f);
  gl.linkProgram(program);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS) && !gl.isContextLost()) {
    throw new GLError(`Failed to link lighting shader "${name}": ${gl.getProgramInfoLog(program)}`);
  }
  const uniforms = new Map<string, WebGLUniformLocation>();
  const nu = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < nu; i++) {
    const info = gl.getActiveUniform(program, i);
    if (!info) continue;
    const loc = gl.getUniformLocation(program, info.name);
    if (loc) uniforms.set(info.name.replace(/\[0\]$/, ''), loc);
  }
  const attribs = new Map<string, number>();
  const na = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES) as number;
  for (let i = 0; i < na; i++) {
    const info = gl.getActiveAttrib(program, i);
    if (info) attribs.set(info.name, gl.getAttribLocation(program, info.name));
  }
  return { program, uniforms, attribs };
}

export interface Target {
  fbo: WebGLFramebuffer;
  tex: WebGLTexture;
  width: number;
  height: number;
  internalFormat: number;
}

export function createTexture(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
  internalFormat: number,
  format: number,
  type: number,
  filter: number = gl.LINEAR,
): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new GLError('Could not create a texture.');
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, format, type, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter === gl.LINEAR_MIPMAP_LINEAR ? gl.LINEAR : filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return tex;
}

export function createTarget(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
  internalFormat: number,
  format: number,
  type: number,
  filter: number = gl.LINEAR,
): Target {
  const tex = createTexture(gl, width, height, internalFormat, format, type, filter);
  const fbo = gl.createFramebuffer();
  if (!fbo) throw new GLError(`Could not create a ${width}×${height} drawing surface.`);
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    gl.deleteFramebuffer(fbo);
    gl.deleteTexture(tex);
    throw new GLError(`Could not create a ${width}×${height} drawing surface.`);
  }
  return { fbo, tex, width, height, internalFormat };
}

export function deleteTarget(gl: WebGL2RenderingContext, t: Target | null): void {
  if (!t) return;
  gl.deleteFramebuffer(t.fbo);
  gl.deleteTexture(t.tex);
}

/** Whether RGBA16F color attachments can be created (EXT_color_buffer_float / _half_float). */
export function halfFloatRenderable(gl: WebGL2RenderingContext): boolean {
  const ext = gl.getExtension('EXT_color_buffer_float') ?? gl.getExtension('EXT_color_buffer_half_float');
  if (!ext) return false;
  try {
    deleteTarget(gl, createTarget(gl, 4, 4, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.NEAREST));
    return true;
  } catch {
    return false;
  }
}
